/**
 * Projects List Page
 * Lists active and past projects for homeowners and designers
 */

import { FolderKanban, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { formatPrice } from "@/lib/format";
import { useProjects } from "../hooks/use-projects";
import { HomeownerProjectsNew } from "./homeowner-projects-new";
import { ProjectsPageRedesign } from "./projects-redesign";

type FilterStatus = "all" | "active" | "completed" | "cancelled";

export function ProjectsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAuthStore();
	const [filter, setFilter] = useState<FilterStatus>("all");

	const isHomeowner = user?.userType === "homeowner";
	const isDesignerOrContractor =
		user?.userType === "designer" || user?.userType === "contractor";

	// Only the homeowner list is fetched here; pros render the redesign below.
	const projectsQuery = useProjects(isHomeowner);
	const projects = projectsQuery.data ?? [];
	const isLoading = projectsQuery.isLoading;

	// Use redesigned projects page for designers and contractors
	if (isDesignerOrContractor) {
		return <ProjectsPageRedesign />;
	}

	// Homeowners always use the new projects page
	if (isHomeowner) {
		return <HomeownerProjectsNew />;
	}

	const filtered = projects.filter((p) => {
		if (filter === "all") return true;
		if (filter === "active")
			return p.status !== "completed" && p.status !== "cancelled";
		if (filter === "completed") return p.status === "completed";
		if (filter === "cancelled") return p.status === "cancelled";
		return true;
	});

	if (isLoading) {
		return (
			<div className="dashboard-container space-y-6">
				<Skeleton className="h-8 w-48" />
				<div className="flex gap-2">
					{[1, 2, 3, 4].map((i) => (
						<Skeleton key={i} className="h-9 w-24" />
					))}
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					{[1, 2, 3, 4].map((i) => (
						<Skeleton key={i} className="h-32 rounded-lg" />
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="dashboard-container space-y-6">
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-foreground">
					{t("projects.title", "My Projects")}
				</h1>
				{isHomeowner && (
					<Button onClick={() => navigate("/projects/new")} size="sm">
						<Plus className="mr-2 h-4 w-4" />
						{t("projects.newProject", "New Project")}
					</Button>
				)}
			</div>

			{/* Filters */}
			{projects.length > 0 && (
				<div className="flex gap-2">
					{(["all", "active", "completed", "cancelled"] as FilterStatus[]).map(
						(status) => (
							<button
								type="button"
								key={status}
								onClick={() => setFilter(status)}
								className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
									filter === status
										? "bg-primary text-primary-foreground"
										: "bg-muted text-muted-foreground hover:bg-muted/80"
								}`}
							>
								{t(
									`projects.filter.${status}`,
									status.charAt(0).toUpperCase() + status.slice(1),
								)}
							</button>
						),
					)}
				</div>
			)}

			{/* Project list */}
			{filtered.length === 0 ? (
				<EmptyState
					icon={FolderKanban}
					title={
						projects.length === 0
							? t("projects.emptyTitle", "No projects yet")
							: t("projects.noMatchingProjects", "No matching projects")
					}
					description={
						projects.length === 0
							? t(
									"projects.emptyDescription",
									"Your active and past projects will appear here.",
								)
							: t("projects.tryDifferentFilter", "Try a different filter.")
					}
				/>
			) : (
				<div className="grid gap-4 sm:grid-cols-2">
					{filtered.map((project) => (
						<div
							key={project.id}
							className="rounded-lg bg-card p-6 text-left shadow-sm transition-shadow hover:shadow-md"
						>
							<button
								type="button"
								onClick={() => navigate(`/projects/${project.id}`)}
								className="w-full text-left"
							>
								<div className="flex items-start justify-between">
									<h3 className="font-semibold text-foreground">
										{project.title}
									</h3>
									<StatusBadge status={project.status} />
								</div>
								<p className="mt-2 text-sm text-muted-foreground">
									{project.scope === "full_home"
										? t("common.fullHome")
										: t("common.partial")}{" "}
									•{" "}
									{project.budgetMin && project.budgetMax
										? `${formatPrice(project.budgetMin)} - ${formatPrice(project.budgetMax)}`
										: t("common.budgetNotSet")}
								</p>
								<p className="mt-1 text-xs text-muted-foreground">
									{t("projects.created", "Created")}{" "}
									{new Date(project.createdAt).toLocaleDateString("en-IN")}
								</p>
								{isHomeowner && (
									<p className="mt-1 text-xs text-muted-foreground">
										Visibility:{" "}
										{project.isPublic
											? "Public (shown when in progress)"
											: "Private"}
									</p>
								)}
							</button>
							{isHomeowner && (
								<div className="mt-4">
									<Button
										variant="outline"
										size="sm"
										onClick={() => navigate(`/projects/${project.id}`)}
									>
										Manage Images & Visibility
									</Button>
								</div>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
}
